import React from 'react';
import { Button, Tooltip, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';

type Props = {
  code: string;
  // Show only the first N lines and put the rest behind 展开全文. Copy always
  // copies the whole text. The install prompts run about twenty lines; shown
  // in full they push step 3 off the first screen.
  clampLines?: number;
};

// 代码块 + 复制按钮。教程页里每个要用户复制的东西都用它（settings.json 片段、
// shell export、jq 检查、建目录、SKILL.md 全文、验证 curl），所以抽出来。
// 这里不写具体次数 —— 那个数字改一次页面就失效一次，已经失准过两轮。
//
// 仓库此前唯一的复制逻辑内联在 components/Settings/ApiTokensManager.tsx 里，
// 这里沿用同样的实现与提示文案（'已复制到剪贴板'），免得两处反馈不一致。
//
// 不接 language 参数：仓库没有语法高亮库，加了也没有消费方。
const CopyBlock: React.FC<Props> = ({ code, clampLines }) => {
  const [expanded, setExpanded] = React.useState(false);
  // A trailing newline is not a line of its own; counting it made 展开全文 say
  // one line more than there is.
  const lines = code.replace(/\n+$/, '').split('\n');
  const clampable = clampLines !== undefined && lines.length > clampLines;
  const clamped = clampable && !expanded;

  const copy = () => {
    navigator.clipboard.writeText(code);
    message.success('已复制到剪贴板');
  };

  return (
    <div>
      <div className="relative">
        {/* whitespace-pre-wrap 而不是 pre：SKILL.md 全文里有 100-180 字符的中文正文行，
            不折行就得横向拖着读。复制走的是 code prop 不是渲染出的 DOM，折行不影响复制内容。
            限高是因为那份全文接近 4KB，否则会把页面撑到几屏。 */}
        <pre className="bg-gray-50 border border-gray-200 rounded p-3 pr-12 text-xs whitespace-pre-wrap max-h-96 overflow-auto">
          {clamped ? lines.slice(0, clampLines).join('\n') : code}
        </pre>
        {clamped && (
          <div className="pointer-events-none absolute inset-x-px bottom-px h-8 rounded-b bg-gradient-to-b from-transparent to-gray-50" />
        )}
        <Tooltip title="复制">
          <Button className="absolute top-2 right-2" size="small" icon={<CopyOutlined />} onClick={copy} />
        </Tooltip>
      </div>
      {clampable && (
        <button
          type="button"
          className="mt-1.5 text-xs text-blue-600 hover:underline"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '收起' : `展开全文（${lines.length} 行）`}
        </button>
      )}
    </div>
  );
};

export default CopyBlock;
